import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ArrowLeft,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ChatWidget } from "@/components/site/ChatWidget";
import { Reveal } from "@/components/site/Reveal";
import { TELEFONO, TELEFONO_HREF } from "@/data/citta";
import { SOTTOPAGINE, CATEGORIA_INFO, VOCE_A_PAGINA } from "@/data/sottopagine";

export default function SubServicePage() {
  const { cat, sub } = useParams();
  const pagina = SOTTOPAGINE[cat]?.[sub];
  const info = CATEGORIA_INFO[cat];

  useEffect(() => {
    if (pagina) {
      document.title = `${pagina.titolo} — SOS Riparazioni 24`;
      window.scrollTo(0, 0);
    }
  }, [pagina]);

  if (!pagina || !info) return <Navigate to="/" replace />;

  const altre = Object.entries(VOCE_A_PAGINA[cat]).filter(([, p]) => p !== `/servizi/${cat}/${sub}`);

  return (
    <div className="min-h-screen bg-obsidian" data-testid={`sottopagina-${sub}`}>
      <Header />
      <main>
        <section className="relative flex min-h-[70vh] items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={info.immagine}
              alt={pagina.titolo}
              className="h-full w-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/70 to-obsidian" />
            <div className="absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]" />
          </div>
          <div className="relative z-10 mx-auto w-full max-w-5xl px-5 pb-16 pt-40 text-center md:px-8">
            <Reveal>
              <Link
                to={`/servizi/${cat}`}
                data-testid="sottopagina-torna-categoria"
                className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-amber-400"
              >
                <ArrowLeft size={15} /> {info.nome} H24
              </Link>
              <p className="overline-label">{info.nome} · Pronto intervento H24</p>
              <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
                {pagina.titolo.split(" in Lombardia")[0]}{" "}
                <span className="text-amber-400">in Lombardia</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                {pagina.intro}
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <a
                  href={TELEFONO_HREF}
                  data-testid="sottopagina-cta-chiama"
                  className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
                >
                  <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
                </a>
                <button
                  type="button"
                  data-testid="sottopagina-cta-chat"
                  onClick={() => window.dispatchEvent(new CustomEvent("apri-chat"))}
                  className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:border-amber-400 hover:text-amber-400"
                >
                  <MessageCircle size={18} /> Chatta con noi
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <Reveal className="text-center">
            <p className="overline-label">Come interveniamo</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Passo dopo passo, <span className="text-amber-400">senza sorprese.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pagina.passi.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <article
                  data-testid={`sottopagina-passo-${i}`}
                  className="h-full rounded-2xl border border-slate-800 bg-slatecard p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50"
                >
                  <span className="font-mono text-3xl font-bold text-amber-500/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{p.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{p.d}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
              {pagina.punti.map((p) => (
                <p
                  key={p}
                  className="flex items-center justify-center gap-2.5 rounded-xl border border-slate-800 bg-slatecard px-5 py-4 text-center text-sm text-slate-200"
                >
                  <CheckCircle2 size={17} className="shrink-0 text-emerald-400" />
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="border-t border-slate-800 bg-abyss py-16 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl px-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              È un'emergenza? <span className="text-amber-400">Partiamo subito.</span>
            </h2>
            <div className="mt-7 flex flex-wrap justify-center gap-4 px-5">
              <a
                href={TELEFONO_HREF}
                data-testid="sottopagina-cta-finale"
                className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
              >
                <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
              </a>
            </div>
            <div className="mt-12 px-5">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">
                Altri interventi · {info.nome}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {altre.map(([voce, path]) => (
                  <Link
                    key={path}
                    to={path}
                    data-testid={`sottopagina-altra-${path.split("/").pop()}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-amber-500 hover:text-amber-400"
                  >
                    {voce} <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
